import { useEffect, useState } from "react";
import api from "../api/client";
import logger from "../lib/logger";
import { toast } from "sonner";
import {
  Plus, Trash2, Sparkles, Camera, Loader2, ClipboardList, AlertTriangle,
} from "lucide-react";

/**
 * Per-site cleaning task templates. Admin-only.
 * - Lists, adds, and removes task templates for the selected site
 * - "Generate with AI" button uses GPT-5.4 to draft a standard schedule
 *   tailored to the site's workplace type
 * - "Photo required" toggle enforces evidence on the worker side
 */
export default function CleaningTasksManager({ sites }) {
  const [siteId, setSiteId] = useState(sites?.[0]?.id || "");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [confirmGenerate, setConfirmGenerate] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const selectedSite = sites.find((s) => s.id === siteId);

  const load = async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/cleaning/task-templates?site_id=${siteId}`);
      setTasks(data.tasks || []);
    } catch (err) {
      logger.error("Load task templates failed:", err);
      toast.error("Failed to load task templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [siteId]);

  const removeTask = async (t) => {
    if (!window.confirm(`Remove "${t.label}" from ${selectedSite?.name}?`)) return;
    try {
      await api.delete(`/cleaning/task-templates/${t.id}`);
      toast.success("Task removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove task");
    }
  };

  const aiGenerate = async (replace) => {
    setGenerating(true);
    setConfirmGenerate(false);
    try {
      const { data } = await api.post("/cleaning/task-templates/generate", {
        site_id: siteId, replace_existing: replace,
      });
      toast.success(`Drafted ${data.inserted} tasks for ${selectedSite?.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  if (!sites?.length) {
    return (
      <div className="text-center py-12 text-gray-500" data-testid="cleaning-tasks-empty">
        Add a workplace site first, then come back here to configure its cleaning checklist.
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-6" data-testid="cleaning-tasks-tab">
      <header>
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-[#002FA7]" />
          <div className="text-xs uppercase tracking-widest text-gray-500">Per-site checklist</div>
        </div>
        <h2 className="text-2xl font-black tracking-tight mt-1">Cleaning task templates</h2>
        <p className="text-sm text-gray-600 mt-2 max-w-2xl">
          The list of tasks workers see on the Checklist screen of the web and mobile apps.
          Tasks marked <strong>📷 photo required</strong> can only be logged with a photo
          attached — perfect for compliance audits.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3 border-b border-gray-200 pb-4">
        <div className="flex-1 min-w-[240px]">
          <label htmlFor="task-tpl-site" className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
            Workplace site
          </label>
          <select
            id="task-tpl-site"
            data-testid="task-tpl-site-select"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 rounded-sm bg-white text-sm font-medium"
          >
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name} {s.type ? `· ${s.type}` : ""}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          data-testid="add-task-template-button"
          className="bg-white border border-[#002FA7] text-[#002FA7] px-4 py-2 rounded-sm flex items-center gap-2 hover:bg-[#002FA7]/5 text-sm font-bold"
        >
          <Plus className="w-4 h-4" /> Add task
        </button>
        <button
          onClick={() => (tasks.length ? setConfirmGenerate(true) : aiGenerate(true))}
          disabled={generating || !siteId}
          data-testid="ai-generate-tasks-button"
          className="bg-[#002FA7] text-white px-4 py-2 rounded-sm flex items-center gap-2 hover:bg-[#002277] text-sm font-bold disabled:opacity-50"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? "Drafting…" : "Generate with AI"}
        </button>
      </div>

      <section className="border border-gray-300 bg-white rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center gap-3 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading templates…
          </div>
        ) : tasks.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <ClipboardList className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p className="font-semibold text-gray-700">No custom tasks yet for this site.</p>
            <p className="text-sm mt-2">
              Workers will see the default checklist for <strong>{selectedSite?.type || "this workplace type"}</strong>.
              Click <em>Generate with AI</em> to draft a tailored schedule, or <em>Add task</em> to write your own.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="task-templates-table">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-300 text-left">
                <th className="px-4 py-3 font-semibold">Task</th>
                <th className="px-4 py-3 font-semibold w-32">Min / week</th>
                <th className="px-4 py-3 font-semibold w-32 text-center">Photo</th>
                <th className="px-4 py-3 font-semibold w-24">Source</th>
                <th className="px-4 py-3 font-semibold w-24 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr key={t.id} className="border-b border-gray-200 last:border-0" data-testid={`task-row-${t.id}`}>
                  <td className="px-4 py-3 font-medium">{t.label}</td>
                  <td className="px-4 py-3 text-gray-700 tabular-nums">{t.min_per_week ?? "—"}</td>
                  <td className="px-4 py-3 text-center">
                    {t.photo_required ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] uppercase tracking-widest font-bold rounded-sm">
                        <Camera className="w-3 h-3" /> Required
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">Optional</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold rounded-sm ${
                      t.source === "ai" ? "bg-[#002FA7]/10 text-[#002FA7]" : "bg-gray-100 text-gray-600"
                    }`}>
                      {t.source}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => removeTask(t)}
                      data-testid={`remove-task-${t.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 border border-red-300 text-red-700 text-xs font-bold tracking-wide hover:bg-red-50 rounded-sm"
                    >
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showAdd && (
        <AddTaskModal
          siteName={selectedSite?.name}
          onClose={() => setShowAdd(false)}
          onCreate={async (body) => {
            try {
              await api.post(`/cleaning/task-templates?site_id=${siteId}`, body);
              toast.success("Task added");
              setShowAdd(false);
              load();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Couldn't add task");
            }
          }}
        />
      )}

      {confirmGenerate && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="confirm-ai-generate-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmGenerate(false); }}
        >
          <div className="bg-white p-6 rounded-sm max-w-md w-full space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-black tracking-tight">Replace existing tasks?</h3>
                <p className="text-sm text-gray-600 mt-2">
                  This site already has <strong>{tasks.length}</strong> custom task{tasks.length === 1 ? "" : "s"}.
                  Choose <em>Replace</em> to wipe them and start fresh from the AI draft, or <em>Append</em>
                  {" "}to add the AI tasks alongside what's already there.
                </p>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => aiGenerate(true)}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-bold tracking-wide hover:bg-red-700 rounded-sm"
                data-testid="ai-replace-button"
              >
                Replace
              </button>
              <button
                onClick={() => aiGenerate(false)}
                className="flex-1 px-4 py-2 bg-[#002FA7] text-white font-bold tracking-wide hover:bg-[#002277] rounded-sm"
                data-testid="ai-append-button"
              >
                Append
              </button>
              <button
                onClick={() => setConfirmGenerate(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 font-bold tracking-wide hover:bg-gray-100 rounded-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddTaskModal({ siteName, onClose, onCreate }) {
  const [label, setLabel] = useState("");
  const [minPerWeek, setMinPerWeek] = useState("");
  const [photoRequired, setPhotoRequired] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) { toast.error("Task label is required"); return; }
    setBusy(true);
    await onCreate({
      label: label.trim(),
      min_per_week: minPerWeek ? Number(minPerWeek) : null,
      photo_required: photoRequired,
    });
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="add-task-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form onSubmit={submit} className="bg-white p-6 rounded-sm max-w-md w-full space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-500">{siteName}</div>
          <h3 className="text-lg font-black tracking-tight">Add cleaning task</h3>
        </div>
        <div>
          <label htmlFor="new-task-label" className="block text-xs uppercase tracking-widest text-gray-500 mb-1">
            Task label
          </label>
          <input
            id="new-task-label"
            data-testid="new-task-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Sanitise treadmill handles"
            required
            className="w-full border border-gray-300 px-3 py-2 rounded-sm"
          />
        </div>
        <div>
          <label htmlFor="new-task-min" className="block text-xs uppercase tracking-widest text-gray-500 mb-1">
            Minimum per week (optional, 1–7)
          </label>
          <input
            id="new-task-min"
            data-testid="new-task-min"
            type="number" min="1" max="7" inputMode="numeric"
            value={minPerWeek}
            onChange={(e) => setMinPerWeek(e.target.value)}
            placeholder="e.g. 5"
            className="w-full border border-gray-300 px-3 py-2 rounded-sm"
          />
        </div>
        <label className="flex items-center gap-2 p-3 border border-gray-300 rounded-sm cursor-pointer hover:bg-gray-50">
          <input
            type="checkbox"
            data-testid="new-task-photo-required"
            checked={photoRequired}
            onChange={(e) => setPhotoRequired(e.target.checked)}
            className="w-4 h-4"
          />
          <Camera className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-medium">Photo required for compliance evidence</span>
        </label>
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={busy}
            data-testid="confirm-add-task"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#002FA7] text-white font-bold tracking-wide hover:bg-[#002277] disabled:opacity-50 rounded-sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add task"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 font-bold tracking-wide hover:bg-gray-100 rounded-sm"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
